import React, {useState} from "react";
import {FieldValues, useForm} from "react-hook-form";
import {DATE_ONLY_DISPLAY} from "./common/util/FormatUtil";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css";
import InputType from "./common/enum/InputType";
import FilterParamType from "./common/enum/FilterParamType";

export interface Filter {
    type: FilterParamType,
    value: any
}

interface FilterFormProps {
    initialFilters: Filter[]
    onFiltersChanged: (filters: Filter[]) => void,
    filters: FilterParamType[],
    selectedFilter: FilterParamType,
}

const FilterForm = (props: FilterFormProps) => {

    const [filters, setFilters] = useState<Filter[]>(props.initialFilters);
    const [selectedFilter, setSelectedFilter] = useState(props.selectedFilter);
    const [selectedFilterDate, setSelectedFilterDate] = useState<Date | null>(null);
    const {register, handleSubmit} = useForm();

    const onSubmit = (data: FieldValues) => {

        const {textValue} = data;

        const extractFilterValue = () => {
            switch (selectedFilter.inputType) {
                case InputType.TEXT: return textValue;
                case InputType.DATE: return selectedFilterDate;
                case InputType.NONE: return selectedFilter.defaultValue();
                default: // TODO set error message
            }
        };

        const val = extractFilterValue();

        if (val) {
            setFilters(prevFilters => {
                const filtersToKeep = prevFilters.filter(f => (f.type !== selectedFilter));
                const newFilter = {
                    type: selectedFilter,
                    value: val
                };
                return [...filtersToKeep, newFilter];
            });
        }
    };

    const removeFilter = (filter: Filter) => {
        setFilters(prevFilters =>
            prevFilters.filter(f => f.type !== filter.type) || []
        );
    };

    const onFilterTypeChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedFilter(FilterParamType.valueOf(event.target.value)!);
    };

    // When our "filters" state changes invoke onFiltersChanged callback
    const { onFiltersChanged } = props;

    React.useEffect(() => {
        onFiltersChanged(filters);
    }, [filters, onFiltersChanged]);

    React.useEffect(() => {
        if (props.initialFilters && props.initialFilters.length > 0) {
            setFilters(props.initialFilters);
        }
    }, [props.initialFilters])

    return (
        <div>
            <div className="row justify-content-center align-items-center">
                <div className="col">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="justify-content-center align-items-center m-3">
                            <div className="row g-3 align-items-end">
                                <div className="col-auto p-1">
                                    <label className="sr-only" htmlFor="inlineFormInput">Filter By</label>
                                    <select
                                        {...register('type', { required: true })}
                                        className="form-select me-sm-2"
                                        id="inlineFormCustomSelect"
                                        onChange={onFilterTypeChanged}
                                        required
                                        autoFocus
                                        defaultValue={props.selectedFilter?.key}
                                    >
                                        {props.filters.map(searchParam => {
                                            return (
                                                <option
                                                    key={searchParam.key}
                                                    value={searchParam.key}
                                                >{searchParam.description}</option>
                                            )}
                                        )}
                                    </select>
                                </div>

                                {selectedFilter.inputType === InputType.TEXT &&
                                    <div className="col-auto p-1">
                                        <label className="sr-only" htmlFor="inlineFormInputGroup">Value</label>
                                        <input
                                            {...register("textValue", {
                                                required: "Required",
                                            })}
                                            type="text"
                                            className="form-control"
                                            placeholder={selectedFilter.placeholder}
                                        />
                                    </div>
                                }

                                {selectedFilter.inputType === InputType.DATE &&
                                    <div className="col-auto p-1">
                                        <label className="sr-only" htmlFor="inlineFormInputGroup" style={{display: 'block'}}>Value</label>
                                        <DatePicker
                                            {...register("dateValue", {})}
                                            showPopperArrow={false}
                                            className="form-control"
                                            selected={selectedFilterDate}
                                            onChange={(date: Date | null) => setSelectedFilterDate(date)}
                                            placeholderText={selectedFilter.placeholder}
                                            dateFormat={DATE_ONLY_DISPLAY}
                                            showMonthDropdown
                                            showYearDropdown
                                            // scrollableYearDropdown
                                        />
                                    </div>
                                }
                                <div className="col-auto p-1 align-bottom" style={{ alignContent: 'end'}}>
                                    <button type="submit" className="btn btn-primary">Add Filter</button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <div className="row justify-content-center">
                <div className="col-lg-8">
                    <p style={{fontSize: '14pt'}}>
                        {filters.map((filter, index) => {
                            return (
                                <span className="badge bg-primary" key={index}>
                                    <span>{filter.type.description} {filter.type.formatForDisplay(filter.value)} &nbsp;</span>
                                    <FontAwesomeIcon style={{verticalAlign: "middle"}} size="2x" onClick={() => removeFilter(filter)} icon={faTimes}/>
                                </span>
                            );
                        }).map((node, index) => <span key={index}>{node} &nbsp; </span>)}
                    </p>
                </div>
            </div>
        </div>
    )

}

export default FilterForm;
